import { IClientLead, ICrmLeadDetailNavState, ILeadInfo } from '../models/lead.model'
import { ICommunicationRecord } from '../models/lead-info.model'
import { ECallStatus, ECommunicationType, ELeadStageTitle } from '@app/core/enum/client.enum'
import { LEAD_STAGES } from '@app/core/constants/app.constants'
import { LEAD_STAGE_NUMBERS } from '@app/core/constants/client.constants'
import {
    CLIENT_LEAD_LIST_STAGE_BADGE_CLASSES,
    CLIENT_LEAD_LIST_DEFAULT_STAGE_BADGE_CLASS,
    CLIENT_LEAD_LIST_STATUS_LABELS,
} from '../constants/client-lead-list.constants'
import { CLIENT_LEAD_LIST_CONSTANTS } from '../constants/client-lead-list.constants'
import { EApiLeadStatus } from '../constants/lead-information.constants'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { EProposalStage } from '@app/core/enum/proposal.enum'
import { IProposal } from '@app/features/oms/models/proposal-summary.model'

const STAGE_NAME_TO_STAGE: Readonly<Record<string, ELeadStageTitle>> = {
    [LEAD_STAGES.ALL_SCHOOLS.toLowerCase().trim()]: ELeadStageTitle.S0,
    [LEAD_STAGES.PROSPECTING.toLowerCase().trim()]: ELeadStageTitle.S1,
    [LEAD_STAGES.PROSPECT_IDENTIFIED.toLowerCase().trim()]: ELeadStageTitle.S2,
    [LEAD_STAGES.DEMO_COMPLETED.toLowerCase().trim()]: ELeadStageTitle.S3,
    [LEAD_STAGES.PROPOSAL_SUBMITTED.toLowerCase().trim()]: ELeadStageTitle.S4,
    [LEAD_STAGES.CONTRACT_NEGOTIATION.toLowerCase().trim()]: ELeadStageTitle.S5,
    [LEAD_STAGES.SSF_SIGNED.toLowerCase().trim()]: ELeadStageTitle.S6,
    [LEAD_STAGES.DEAL_WON.toLowerCase().trim()]: ELeadStageTitle.S7,
    [LEAD_STAGES.LEAD_LOST.toLowerCase().trim()]: ELeadStageTitle.S8,
    [LEAD_STAGES.LEAD_DEFERRED.toLowerCase().trim()]: ELeadStageTitle.S9,
}

/**
 * Build the router state object used when navigating to the CRM lead detail page.
 */
export function crmLeadDetailNavState(
    lead: Pick<ILeadInfo, 'id' | 'order_id' | 'order_status'>,
): ICrmLeadDetailNavState {
    return {
        crmLeadId: Number(lead.id),
        crmOrderId: lead.order_id ?? null,
        crmOrderStatus: lead.order_status ?? null,
    }
}

/**
 * Truncate text if it exceeds max length
 */
export function truncateText(
    text: string,
    maxLength: number = CLIENT_LEAD_LIST_CONSTANTS.MAX_TRUNCATE_LENGTH,
): string {
    if (!text || text.length <= maxLength) {
        return text
    }
    return `${text.substring(0, maxLength)}...`
}

/**
 * Format products array as truncated string
 */
export function formatProducts(
    products: string[],
    maxLength: number = CLIENT_LEAD_LIST_CONSTANTS.MAX_TRUNCATE_LENGTH,
): string {
    if (!products?.length) {
        return CLIENT_LEAD_LIST_CONSTANTS.EMPTY_PRODUCTS_DISPLAY
    }
    const productsStr = products.join(', ')
    return truncateText(productsStr, maxLength)
}

/**
 * Formats products into 2 lines:
 * - line 1: first N products (default 2)
 * - line 2: remaining products (if any)
 *
 * Newline is preserved in UI via CSS `white-space: pre-line`.
 */
export function formatProductsTwoLine(
    products: string[],
    perLine: number = CLIENT_LEAD_LIST_CONSTANTS.PRODUCTS_PER_LINE,
): string {
    if (!products?.length) {
        return CLIENT_LEAD_LIST_CONSTANTS.EMPTY_PRODUCTS_DISPLAY
    }

    const firstLine = products.slice(0, perLine).join(', ')
    const rest = products.slice(perLine).join(', ')
    return rest ? `${firstLine}\n${rest}` : firstLine
}

/**
 * Full products string for tooltip/title.
 */
export function formatProductsFull(products: string[]): string {
    if (!products?.length) {
        return CLIENT_LEAD_LIST_CONSTANTS.EMPTY_PRODUCTS_DISPLAY
    }
    return products.join(', ')
}

/**
 * Get initials from a name
 */
export function getInitials(name: string): string {
    if (!name) return ''
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
}

/**
 * Map stage code to stage ID
 */
export function mapStageToStageId(stage: string): number | null {
    const stageMap: Record<string, number> = {
        [ELeadStageTitle.S0]: 0,
        [ELeadStageTitle.S1]: 1,
        [ELeadStageTitle.S2]: 2,
        [ELeadStageTitle.S3]: 3,
        [ELeadStageTitle.S4]: 4,
        [ELeadStageTitle.S5]: 5,
        [ELeadStageTitle.S6]: 6,
        [ELeadStageTitle.S7]: 7,
        [ELeadStageTitle.S8]: 8,
        [ELeadStageTitle.S9]: 9,
    }
    return stageMap[stage] ?? null
}

/**
 * Map backend stage_name (e.g. from user-leads API) to ELeadStageTitle.
 * Uses LEAD_STAGES; case-insensitive, trimmed. Defaults to S1 when no match.
 */
export function mapStageNameToStage(stageName: string): ELeadStageTitle {
    const normalized = (stageName ?? '').trim().toLowerCase()
    if (!normalized) return ELeadStageTitle.S1
    return STAGE_NAME_TO_STAGE[normalized] ?? ELeadStageTitle.S1
}

/**
 * Map stage ID to stage code
 */
export function mapStageIdToStage(stageId?: number): ELeadStageTitle {
    const stageIdMap: Record<number, ELeadStageTitle> = {
        0: ELeadStageTitle.S0,
        1: ELeadStageTitle.S1,
        2: ELeadStageTitle.S2,
        3: ELeadStageTitle.S3,
        4: ELeadStageTitle.S4,
        5: ELeadStageTitle.S5,
        6: ELeadStageTitle.S6,
        7: ELeadStageTitle.S7,
        8: ELeadStageTitle.S8,
        9: ELeadStageTitle.S9,
    }
    return stageIdMap[stageId ?? 1] ?? ELeadStageTitle.S1
}

/**
 * Lead is stage-qualified for My Leads / Team Pipeline when it is S1 or above.
 * This excludes only S0, matching the S1–S9 requirement.
 */
export function isStageQualifiedPipelineLead(lead: Pick<ILeadInfo, 'stage'>): boolean {
    const match = String(lead.stage ?? '').match(REGEX_PATTERNS.CRM_LEAD_STAGE_NUMBER_CAPTURE)
    const stageNum = match ? Number(match[1]) : null
    return stageNum !== null && stageNum >= LEAD_STAGE_NUMBERS.S1
}

/** Filters leads down to stage-qualified (S1+) while preserving input order. */
export function filterStageQualifiedPipelineLeads<T extends Pick<ILeadInfo, 'stage'>>(
    leads: T[],
): T[] {
    return (leads ?? []).filter((lead) => isStageQualifiedPipelineLead(lead))
}

/**
 * For lost / deferred leads, show canonical labels from status instead of
 * raw stage codes or API `stage_name`.
 */
export function getClientLeadLostDeferredLabelFromParts(
    leadStatus: EApiLeadStatus | null | undefined,
): string | null {
    if (leadStatus === EApiLeadStatus.DROPPED) {
        return LEAD_STAGES.LEAD_LOST
    }
    if (leadStatus === EApiLeadStatus.ON_HOLD) {
        return LEAD_STAGES.LEAD_DEFERRED
    }
    return null
}

/** Stage Name column text (Lead Lost / Lead Deferred from status, not raw API stage name). */
export function getClientLeadStageNameDisplay(lead: IClientLead): string {
    const lostDeferred = getClientLeadLostDeferredLabelFromParts(lead.leadStatus)
    if (lostDeferred !== null) return lostDeferred
    return lead.stageName || '-'
}

/**
 * Get status badge class for a stage
 */
export function getStatusBadgeClass(stage: string): string {
    return (
        CLIENT_LEAD_LIST_STAGE_BADGE_CLASSES[
            stage as keyof typeof CLIENT_LEAD_LIST_STAGE_BADGE_CLASSES
        ] || CLIENT_LEAD_LIST_DEFAULT_STAGE_BADGE_CLASS
    )
}

export function isLoggedInUserLeadAssignee(
    assignedToUserId: string | null | undefined,
    loggedInUserId: string | undefined,
): boolean {
    const normalizedAssignedTo = assignedToUserId?.trim()
    const normalizedLoggedInUserId = loggedInUserId?.trim()
    if (!normalizedAssignedTo || !normalizedLoggedInUserId) {
        return false
    }
    return normalizedAssignedTo === normalizedLoggedInUserId
}

export function isLoggedInUserAssigneeOnAnyClientLead(
    leads: IClientLead[],
    loggedInUserId: string | undefined,
): boolean {
    if (!loggedInUserId?.trim()) {
        return false
    }
    if (!leads?.length) {
        return true
    }
    return leads.some((lead) => isLoggedInUserLeadAssignee(lead.assignedTo?.id, loggedInUserId))
}

export function getClientLeadOwnerDisplayName(lead: IClientLead): string {
    return lead.assignedTo?.name?.trim() || '-'
}

/**
 * Check if a lead is active (not dropped)
 */
export function isActiveLead(lead: IClientLead): boolean {
    return lead.statusLabel !== CLIENT_LEAD_LIST_STATUS_LABELS.LEAD_DROPPED
}

/**
 * New lead is allowed when every existing lead is S6+ or Lead Lost (dropped).
 */
export function canCreateNewLead(leads: IClientLead[]): boolean {
    return (leads ?? []).every((lead) => {
        const match = String(lead.stage ?? '').match(REGEX_PATTERNS.CRM_LEAD_STAGE_NUMBER_CAPTURE)
        const stageNum = match ? Number(match[1]) : null
        const isLost = lead.leadStatus === EApiLeadStatus.DROPPED
        return (stageNum !== null && stageNum > LEAD_STAGE_NUMBERS.S5) || isLost
    })
}

/**
 * S0 → S1: at least one qualifying communication — call with status Call Connected,
 * any in-person record, or any email record.
 *
 * List API may return `call_status` and/or `status` (record-call-modal sets `status` on create);
 * accept either so eligibility matches what users actually logged.
 */
export function leadHasQualifyingCommunicationForMoveToS1(
    records: ICommunicationRecord[] | undefined,
): boolean {
    if (!records?.length) {
        return false
    }
    const connectedLabel = ECallStatus.CALL_CONNECTED.trim().toLowerCase()
    return records.some((record) => {
        const typeNorm = String(record.type ?? '')
            .trim()
            .toLowerCase()
        if (typeNorm === ECommunicationType.IN_PERSON || typeNorm === ECommunicationType.EMAIL) {
            return true
        }
        if (typeNorm !== ECommunicationType.CALL) {
            return false
        }
        const rawStatus = record.call_status ?? (record.status != null ? String(record.status) : '')
        const status = rawStatus.trim().toLowerCase()
        return status.length > 0 && status === connectedLabel
    })
}

/**
 * Filter leads by text-based stage name
 */
export function filterLeadsByStageName(leads: IClientLead[], selectedStage: string): IClientLead[] {
    if (!selectedStage) {
        return [...leads]
    }

    // If stage maps to stage_id, it's already filtered by API
    if (mapStageToStageId(selectedStage) !== null) {
        return [...leads]
    }

    const stageNames = CLIENT_LEAD_LIST_CONSTANTS.STAGE_NAMES
    const isValidStageName = stageNames.includes(selectedStage as (typeof stageNames)[number])

    if (!isValidStageName) {
        return [...leads]
    }

    return leads.filter(
        (lead) => lead.stageName === selectedStage || lead.statusLabel === selectedStage,
    )
}

/** Distinct positive `order_id` values from kanban/table lead rows (for OMS proposal stage fetch). */
export function distinctPositiveOrderIdsFromLeads(leads: ILeadInfo[]): number[] {
    const ids = leads
        .map((lead) => lead.order_id)
        .filter((id): id is number => id != null && id > 0)
    return [...new Set(ids)]
}

/** Build the map expected by `LeadStatusColumnComponent` from `getProposalsByOrderIds` results. */
export function buildOrderIdToProposalStageMap(
    proposals: Pick<IProposal, 'id' | 'stage'>[],
): Map<number, EProposalStage> {
    const map = new Map<number, EProposalStage>()
    for (const proposal of proposals) {
        const id = parseInt(proposal.id, 10)
        if (!Number.isNaN(id)) {
            map.set(id, proposal.stage)
        }
    }
    return map
}
